import Collaboration from '@tiptap/extension-collaboration'
import { absolutePositionToRelativePosition, ySyncPluginKey } from '@tiptap/y-tiptap'
import { EditorContent, useEditor } from '@tiptap/react'
import { registerEditor } from '../../collab/history.js'
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { CitationPopover } from '../../citation/CitationPopover.js'
import type { Language } from '@eformulieren/engine'
import CollaborationCaret from '@tiptap/extension-collaboration-caret'
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  BookMarked,
  Code,
  Code2,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Image as ImageIcoon,
  Italic,
  List,
  ListChecks,
  ListOrdered,
  MessageSquarePlus,
  Minus,
  Pilcrow,
  Plus,
  Quote,
  Search,
  Sigma,
  Strikethrough,
  Subscript as SubscriptIcoon,
  Superscript as SuperscriptIcoon,
  SquareFunction,
  Table as TableIcoon,
  Trash2,
  Underline as UnderlineIcoon,
} from 'lucide-react'
import { attributionOf } from '../../collab/attribution.js'
import { caretLabelText, caretUser, characterCaret, selectionOf } from '../../collab/employee.js'
import { COMMENT_MARK } from '../../comments/CommentMark.js'
import { CommentDialog } from '../../comments/CommentDialog.js'
import { DomSelectionSync, syncSelectionFromDom } from './richtext/domSelection.js'
import { ReadOnlyGuard } from './richtext/readOnlyGuard.js'
import { useHoldDuringPress } from './richtext/holdDuringPress.js'
import type { CommentRange } from '../../document/commentRange.js'
import { encodeRelativePosition } from '../../document/commentRange.js'
import { usePlainText } from '../../document/useSectionDocument.js'
import { Counter } from '../Counter.js'
import { RefusedCharactersNotice, useRefusedCharacters } from './refusedCharacters.js'
import { withoutForbiddenInSlice } from './richtext/characters.js'
import { inLanguage } from '../../language/text.js'
import { Find } from './richtext/find.js'
import { FindBar } from './richtext/FindBar.js'
import { highlight } from '../../shell/dialogPlace.js'
import { preloadFormulaPanel, useFormulaPanel } from './richtext/formulaPanelLoader.js'
import { RICH_TEXT_EXTENSIONS } from './richtext/schema.js'
import { Toolbar, type ToolbarPart } from './richtext/Toolbar.js'
import type { FragmentProps } from './kinds.js'

/**
 * Rich text: de kern van het formulier (ontwerp 6.2).
 *
 * Vier dingen die vastliggen.
 *
 * **De eigen historie van de editor staat uit** (`undoRedo: false` op de StarterKit).
 * De samenwerklaag levert undo en redo; twee stapels tegelijk botsen, en dan maakt
 * een undo van de een het werk van de ander ongedaan (valkuil 5 uit bijlage C.4). In
 * M2 is er nog geen samenwerklaag en dus nog geen undo; die komt met M3.
 *
 * **De editor hangt aan het fragment van dit veld** en niet aan het hele document
 * (ontwerp 6.8): één `Y.XmlFragment` per rich-text-veld, met de veld-id als naam. De
 * control krijgt alleen dat fragment aangereikt.
 *
 * **De teller telt platte tekst en geen opmaak.** Hij gebruikt dezelfde functie als
 * de uitgeschreven stand van een revisie (`plainTextOfFragment`), zodat het getal
 * dat de invuller ziet en het getal waarop de woordgrens indienen tegenhoudt
 * hetzelfde zijn (R-12, R-13).
 *
 * **Klembord blijft gewoon werken, zoeken krijgt een eigen balk** (R-166, G047). Geen handler
 * onderschept kopiëren, knippen of plakken. Zoeken wel, en alleen met de focus in dit veld:
 * Ctrl/Cmd+F opent dan de zoekbalk van dit veld (`richtext/FindBar.tsx`), ook in een veld dat
 * alleen te lezen is. De zoekfunctie van de browser vindt in een lange tekst wel iets, maar telt
 * over de hele pagina en ziet een treffer in dit veld niet als iets van dit veld; buiten een veld
 * blijft Ctrl+F gewoon van de browser. De treffers zijn een markering en geen wijziging van het
 * document (`richtext/find.ts`).
 *
 * Twee einden zijn met opzet nog open, en staan als zodanig in `docs/dekking.md`: een
 * figuur krijgt zijn bytes pas met de content-addressed opslag (M3), en de
 * literatuurlijst die uit de bronvermeldingen groeit is een vast component (M7).
 *
 * **De cursor van een medeschrijver komt uit de awareness** (R-72, M4.12d). Die staat los van het
 * document: hij hoort bij de verbinding, is vluchtig, en wordt door elke client zelf geschreven.
 * De naam erin is daarom decoratie en nooit identiteit (beveiligingsanalyse PortaalPOC); zie de
 * kop van `Presencebalk.tsx`. Zonder awareness is er geen cursor en werkt het veld gewoon.
 *
 * **De opmerkingen hangen aan de tekst en niet aan het veld** (R-73, M4.12c). Het anker is een
 * mark in hetzelfde fragment, dus hij schuift, splitst en verdwijnt mee bij gelijktijdig
 * bewerken (R-72). Het gesprek staat los, in de dradenwortel van het sectiedocument, en het
 * paneel eronder toont het. Twee dingen zijn daarbij een keuze van dit bestand: de knop plaatst
 * alleen een anker als er iets is geselecteerd, want een opmerking bij niets is nergens aan te
 * hangen, en het paneel staat er alleen als er opmerkingen zijn.
 */

export interface RichtextProps extends FragmentProps {
  /** Waar een figuur uit komt. In M2 de harnas of een test, vanaf M3 de opslag. */
  readonly chooseFigure?: () => Promise<{ hash: string; bestandsnaam: string; grootteBytes: number } | undefined>
  /**
   * Welke bronnen de invuller aan een nieuwe citatie hangt, of niets bij annuleren (R-50).
   *
   * Levert bron-id's uit de bronnenopslag van deze sectie, geen zichtbaar label: dat label is
   * afgeleid (zie `richtext/referentie.ts`) en dus niet aan de kiezer.
   */
  readonly chooseReference?: (options?: { readonly zoeken?: boolean }) => Promise<readonly string[] | undefined>
  /** Een nieuw citatie-id, van de afnemer: een id maken is niet aan een control (vgl. `nieuwDraadId`). */
  readonly newCitationId?: () => string
  /**
   * Per `citatieId` het label van een bronvermelding in de tekst, of niets (R-50).
   *
   * Van de afnemer, want de nummering en de disambiguatie lopen over de secties van de laag in de
   * projectie (`useSectionCitations`), en dit veld kent alleen zijn eigen fragment. Zonder deze prop
   * toont een bronvermelding de plaatshouder van `richtext/reference.ts`.
   */
  readonly citationLabels?: ReadonlyMap<string, string>
  /**
   * Per `citatieId` de regels uit de literatuurlijst van de bronnen die ze citeert, als HTML van
   * citeproc, of niets (R-50). Voor de popover met de volledige bronvermelding; uit dezelfde
   * uitkomst als `citationLabels`.
   */
  readonly citationEntries?: ReadonlyMap<string, readonly string[]>
  /**
   * Het paneel dat bij `chooseReference` hoort, of niets (R-50).
   *
   * Van de afnemer, net als `chooseReference` zelf: de kiezer heeft de bronnenopslag van de hele
   * sectie nodig (niet alleen dit fragment) en moet maar één keer bestaan per sectie, ook als
   * er meerdere rich-text-velden zijn. Deze control tekent hem alleen op zijn plek, onder het
   * veld naast `Draadpaneel`, naar hetzelfde niet-modale patroon (R-158).
   */
  readonly sourcePanel?: ReactNode
  /** Een nieuw draad-id, van de afnemer: een id maken is niet aan een control. */
  readonly nieuwDraadId?: () => string
  /**
   * Een opmerking bij de selectie, voor wie in dit veld schrijft (R-73): het draadje in het document;
   * het anker zet dit veld zelf. Zonder deze prop en zonder `onOpmerkingLangsPod` geen knop Opmerking.
   */
  readonly onNieuweOpmerking?: (
    thread: { id: string; veldId: string; citaat: string },
    plainText: string,
  ) => void
  /**
   * Een opmerking bij de selectie, voor wie dit veld alleen leest (besluit 8, keuze 2 van 1 november
   * 2026): het bereik gaat als twee relatieve posities naar de pod, die anker en draadje zet
   * (`markCommentRange`). Het document in deze browser blijft onaangeroerd tot de update van de pod.
   */
  readonly onOpmerkingLangsPod?: (bereik: CommentRange, plainText: string) => Promise<{ readonly draad: string }>
  /**
   * De samenwerkverbinding van deze sectie, voor de cursor van een medeschrijver (R-72).
   *
   * Van de schil en niet uit het fragment: awareness hoort bij de verbinding en niet bij het
   * document, en deze control kent geen verbinding. Zonder deze prop is er geen cursor.
   *
   * `anoniem` als deze deelnemer op dit document als medewerker van NWO meedoet (R-66a, B6): dan
   * zet de cursor het merkteken in de awareness en geen naam of kleur uit zijn persoon.
   */
  readonly presence?: {
    readonly provider: unknown
    readonly naam: string
    readonly anoniem?: boolean
    /** Hoe een ander met een persoonssleutel bij zijn cursor heet; zie `presentName`. */
    readonly nameOf?: (person: string) => string
  }
}

export function Richtext({
  veld: field,
  taal: language,
  koppeling: link,
  fragment,
  bewerkbaar: editable,
  limieten: limits,
  chooseFigure,
  chooseReference,
  newCitationId,
  citationLabels,
  citationEntries,
  sourcePanel,
  presence,
  nieuwDraadId: newThreadId,
  onNieuweOpmerking: onNewComment,
  onOpmerkingLangsPod: onCommentViaPod,
  onOwnChange,
}: RichtextProps): ReactNode {
  // De teller volgt het document en niet alleen de eigen toetsaanslagen: een
  // medeschrijver die woorden toevoegt hoort de teller ook te laten bewegen (R-72).
  const plainText = usePlainText(fragment)
  // Hoe een medeschrijver bij zijn cursor heet; zie de `render` van de cursor hieronder.
  const caretNameOf = useRef(presence?.nameOf)
  caretNameOf.current = presence?.nameOf
  /**
   * De labels van de cursors in beeld, met de `user` waaruit ze zijn getekend.
   *
   * y-tiptap geeft elke cursor als sleutel zijn clientId (`Decoration.widget` met `key`), en dan
   * hergebruikt ProseMirror het element en roept `render` niet opnieuw aan. Het label hield zo de naam
   * van het moment dat de ander binnenkwam: "Een andere deelnemer" zolang het overzicht van de delingen
   * nog niet gelezen was, terwijl de sectie daarna zijn adres toonde (live test van 3 november 2026).
   * Daarom zet het effect hieronder de tekst opnieuw als `nameOf` verandert. De persoonssleutel staat
   * hier in het geheugen en niet in een attribuut, want in de DOM hoort hij niet (R-154).
   */
  const caretLabels = useRef(new Map<HTMLElement, Record<string, unknown>>())
  useEffect(() => {
    for (const [label, user] of caretLabels.current) {
      if (!label.isConnected) {
        caretLabels.current.delete(label)
        continue
      }
      const text = caretLabelText(user, language, presence?.nameOf)
      if (label.textContent !== text) label.textContent = text
    }
  }, [presence?.nameOf, language])
  const counterId = `${link.id}-teller`
  /**
   * Waar de werkbalk is, voor wie het veld met een schermlezer binnenkomt. Zonder focus staat de
   * balk er niet (ontwerp van het invulscherm, 29 oktober 2026: geen lege strook met een hint boven
   * elk veld); deze beschrijving zegt dat hij er bij focus is en hoe je erheen gaat.
   */
  const toolbarHintId = `${link.id}-werkbalk`

  /**
   * De attributiecontext van dit sectiedocument, één per document (ontwerp 6.9).
   *
   * Uit het fragment en niet als prop: elke rich-text-editor in dezelfde sectie hoort dezelfde
   * `Y.PermanentUserData` te krijgen, anders klopt de attributie niet. Een fragment zonder
   * document komt alleen voor in een test die er los een maakt.
   */
  const attribution =
    fragment.doc === null
      ? undefined
      : (({ permanentUserData, colorMapping }) => ({ permanentUserData, colorMapping }))(
          attributionOf(fragment.doc),
        )

  /**
   * De opmerking in de maak: waarover hij gaat, en waar in de tekst.
   *
   * Lokaal en niet in het document, want een concept is nog niemands inhoud. De posities gaan
   * mee omdat de selectie kan verschuiven tussen de knop en het eerste bericht.
   */
  const [draft, setDraft] = useState<
    { id: string; citaat: string; van: number; tot: number } | undefined
  >(undefined)

  /**
   * De formule in de maak: nieuw (geen `pos`) of een bestaande knoop die wordt bewerkt.
   *
   * Lokaal en niet in het document, om dezelfde reden als `concept` hierboven: een concept is
   * nog niemands inhoud. `pos` komt uit `handleClickOn` hieronder en niet uit `Mathematics`'
   * eigen `onClick`-optie: die optie hoort bij `RICH_TEXT_EXTENSIONS` (`richtext/schema.ts`),
   * een module-brede lijst die `richTextSchema()` ook buiten een gemonteerde editor om
   * gebruikt (R-50). Een klik is per-editor-instantie, dus configureren we hem hier en niet
   * daar — dezelfde reden dat `Collaboration` en `CollaborationCaret` niet in die lijst staan.
   */
  const [formula, setFormula] = useState<
    { modus: 'inline' | 'blok'; latex: string; pos?: number } | undefined
  >(undefined)
  const { Panel: FormulaPanel, failed: formulaPanelFailed, retry: retryFormulaPanel } = useFormulaPanel(formula !== undefined)

  /**
   * Welke ankers nu in de tekst staan.
   *
   * Uit de editorstand en niet uit het draadje: een draadje weet niet of zijn anker nog bestaat,
   * en dat is precies het geval dat het citaat moet opvangen. Bij elke hertekening opnieuw
   * gelezen, want een medeschrijver kan de tekst waar een opmerking aan hangt weghalen.
   */
  /**
   * Of het veld nu bewerkbaar is, voor de attributen van het vlak. Via een ref: `editorProps` gaat
   * één keer mee bij het aanmaken, en een veld kan van rol wisselen (zie `setEditable` hieronder).
   */
  const editableRef = useRef(editable)
  editableRef.current = editable

  /**
   * Alleen lezen, en toch tekst selecteren voor een opmerking (besluit 8, keuze 2 van 1 november 2026,
   * R-140). Een vlak met `contenteditable="false"` laat zich met het toetsenbord niet selecteren: er is
   * geen cursor, dus Shift en een pijltje doen niets. Voor een meelezer die een opmerking kan plaatsen
   * is het vlak daarom bewerkbaar voor ProseMirror, met een slot op elke wijziging van de tekst
   * (`ReadOnlyGuard` hieronder) en `aria-readonly` voor de schermlezer. Een wijziging van de
   * samenwerklaag gaat wel door, anders zag de meelezer de tekst van de schrijvers niet meer.
   */
  const selectable = !editable && onCommentViaPod !== undefined
  const selectableRef = useRef(selectable)
  selectableRef.current = selectable
  const guardRef = useRef(!editable)
  guardRef.current = !editable

  /** Of er bij plakken een teken is weggelaten (`refusedCharacters.tsx`); via een ref, om dezelfde reden. */
  const { refused, report } = useRefusedCharacters()
  const reportRef = useRef(report)
  reportRef.current = report

  /** Wat een getypte `@` doet; via een ref, want `editorProps` wordt één keer gelezen. */
  const atTrigger = useRef<((at: number) => void) | undefined>(undefined)
  const editor = useEditor({
    // Zonder dit hertekent `@tiptap/react` deze component NIET vanzelf op een transactie
    // (het pakket noemt dit zelf "legacy", maar biedt het bewust aan voor precies dit
    // gebruik: `editor.isActive()`/`editor.can()` rechtstreeks in de renderbody lezen, wat
    // deze balk overal doet). Zonder deze vlag "werkte" het toch, maar alleen toevallig: een
    // echte tekstwijziging loopt via Yjs door `usePlainText` (die hertekent zelf al), en de
    // állereerste focus van het veld gaf toevallig ook een hertekening via een ander component
    // hogerop. Kwam een knop zoals "Vet" pas ná die eerste focus in beeld (zie `hasFocus`
    // hierboven), dan bleef `aria-pressed` na een klik hangen op de oude waarde: een echte,
    // aan het licht gekomen fragiliteit, geen eigenaardigheid van de focus-balk zelf.
    shouldRerenderOnTransaction: true,
    extensions: [
      // StarterKit, Mathematics, Figuur, Referentie, YChangeMark, YChangeAttribute en
      // Opmerkingmark: één gedeelde lijst (`richtext/schema.ts`), ook gebruikt waar een fragment
      // buiten deze editor om wordt gelezen of geschreven (R-50). Twee losse lijsten zouden vroeg
      // of laat uiteenlopen, en dat is precies valkuil 2: een knooptype dat in het ene schema wél
      // staat en in het andere niet, verdwijnt stil uit het levende document.
      ...RICH_TEXT_EXTENSIONS,
      // Zoeken in dit veld (R-166): alleen decoraties, geen knoop of mark, en dus niet in de gedeelde
      // lijst hierboven, die ook buiten een editor het schema is (`richtext/find.ts`).
      Find,
      // Het slot voor een vlak dat alleen te lezen is maar wel te selecteren (`selectable` hierboven).
      ReadOnlyGuard.configure({ actief: () => guardRef.current }),
      // De laatste stap van een selectie die de browser nog niet aan de editor had gemeld (`richtext/domSelection.ts`).
      DomSelectionSync,
      // `ySyncOptions` geeft de attributiecontext mee: wie schreef wat, en met welke kleur
      // (ontwerp 6.9). Zonder dat zoekt y-tiptap bij het renderen van een stand uit de
      // geschiedenis geen auteur op en kiest hij zelf een kleur.
      Collaboration.configure({
        fragment,
        ...(attribution === undefined ? {} : { ySyncOptions: attribution }),
      }),
      // De cursor van een medeschrijver, alleen als er een verbinding is (R-72). Anders dan de
      // mark hierboven mag deze extensie er wél ontbreken: hij schrijft niets in het document en
      // leest niets uit een opgeslagen stand, dus zijn afwezigheid kan geen inhoud kwijtmaken.
      ...(presence === undefined
        ? []
        : [
            CollaborationCaret.configure({
              provider: presence.provider,
              // Wat de cursor in de awareness zet en hoe hij er bij de anderen uitziet, ook als
              // medewerker van NWO (R-66a, R-139, B6): zie `collab/medewerker.ts`.
              user: caretUser(presence.naam, presence.anoniem === true),
              // Via de ref, want de extensie leest `render` één keer en de nummering van de
              // deelnemers groeit met wie er verbindt.
              render: (user: Record<string, unknown>) => {
                const caret = characterCaret(user, language, (person) => caretNameOf.current?.(person))
                const label = caret.querySelector<HTMLElement>('.collaboration-carets__label')
                if (label !== null) caretLabels.current.set(label, user)
                return caret
              },
              // De selectie van een ander in de lichte tint van zijn kleur: zie `selectionOf`.
              selectionRender: selectionOf,
            }),
          ]),
    ],
    editable: editable || selectable,
    onUpdate: ({ transaction }) => {
      // Een wijziging die van de samenwerklaag komt is niet van deze invuller:
      // y-prosemirror zet daar `isChangeOrigin` op. Zonder dat onderscheid zou de
      // herkomst van een medeschrijver op naam van de lezer komen te staan.
      const fromElsewhere = transaction.getMeta(ySyncPluginKey) as { isChangeOrigin?: boolean } | undefined
      if (fromElsewhere?.isChangeOrigin === true) return
      onOwnChange?.()
    },
    editorProps: {
      // Een functie en geen vast object: ProseMirror leest de attributen bij elke update opnieuw, en
      // zo volgen ze `setEditable` hieronder.
      attributes: () => ({
        id: link.id,
        class: 'ef-richtext__vlak',
        // Een contenteditable is geen labelbaar element, dus de kop van de veldschil
        // hangt hier met aria-labelledby aan vast (zie `labelViaHeader`).
        ...(link['aria-labelledby'] === undefined
          ? {}
          : { 'aria-labelledby': link['aria-labelledby'] }),
        'aria-describedby': [link['aria-describedby'], limits === undefined ? undefined : counterId, editableRef.current || selectableRef.current ? toolbarHintId : undefined]
          .filter((part): part is string => part !== undefined)
          .join(' '),
        'aria-multiline': 'true',
        role: 'textbox',
        // Een vlak dat alleen te lezen is, is zonder `tabindex` geen stop voor het toetsenbord, en
        // dan kan de toetser er niet in zoeken (R-166) of het niet met een schermlezer als één
        // tekstvak vinden. Met `aria-readonly` weet die dat hij er niet in kan typen (R-139).
        ...(editableRef.current ? {} : { tabindex: '0', 'aria-readonly': 'true' }),
      }),
      // Klik op een bestaande formule heropent het paneel, voorgevuld (R-14). Ongeconfigureerd
      // laat `Mathematics` (in `RICH_TEXT_EXTENSIONS`) een klik gewoon doorbubbelen naar de
      // editor: zijn eigen node view zet alleen een `stopPropagation`-listener als er een
      // `onClick`-optie is meegegeven, wat hier bewust niet gebeurt (zie het commentaar bij
      // `formule` hierboven).
      // `@` opent de bronnenkiezer, zoals in CitatenPOC (`suggestion.ts`, opdracht van 30 oktober
      // 2026, R-14, R-50): aan het begin van een regel of na een spatie, en niet midden in een woord,
      // zodat een e-mailadres een e-mailadres blijft. De `@` komt eerst gewoon in de tekst; de
      // kiezer vervangt hem door de bronvermelding, en annuleren laat hem staan.
      // Plakken en slepen zonder de tekens die niet kunnen worden verstuurd (koppelvlak hoofdstuk 3).
      transformPasted: (slice) => {
        const { slice: clean, removed } = withoutForbiddenInSlice(slice)
        reportRef.current(removed)
        return clean
      },
      handleTextInput: (view, from, _to, text) => {
        if (text !== '@' || atTrigger.current === undefined || !view.editable) return false
        const $from = view.state.doc.resolve(from)
        const offset = $from.parentOffset
        const before = offset === 0 ? '' : $from.parent.textBetween(offset - 1, offset, undefined, '\ufffc')
        if (before !== '' && !/\s/.test(before)) return false
        const open = atTrigger.current
        setTimeout(() => open(from), 0)
        return false
      },
      handleClickOn: (_view, _pos, node, nodePos) => {
        if (node.type.name !== 'inlineMath' && node.type.name !== 'blockMath') return false
        setFormula({
          modus: node.type.name === 'blockMath' ? 'blok' : 'inline',
          latex: String(node.attrs['latex'] ?? ''),
          pos: nodePos,
        })
        return true
      },
    },
  })

  /**
   * `useEditor`'s `editable` optie geldt alleen bij het aanmaken van de editor: een lezer die
   * van rol wisselt op hetzelfde gemounte veld (het voorbeeld van de configurator, dat één
   * document per sectie over een rolwissel heen bewaart) hield de editor anders bewerkbaar
   * zoals bij de eerste keer tonen, ook als `bewerkbaar` intussen `false` werd.
   *
   * **Zonder update-melding** (het tweede argument). Tiptap meldt bij `setEditable` standaard een
   * update, en `onUpdate` hierboven nam die aan als eigen wijziging: de schil stempelde dan de
   * herkomst van het veld op wie keek en schreef dat in het sectiedocument, terwijl er niets aan de
   * inhoud veranderde. Gevonden toen de pagina van de pod bij een verlopen sessie alles alleen-lezen
   * zet (`apps/runtime/pagina/session.ts`).
   */
  useEffect(() => {
    if (editor !== null && editor.isEditable !== (editable || selectable)) editor.setEditable(editable || selectable, false)
  }, [editor, editable, selectable])

  /**
   * Aanmelden bij het register van de geschiedenis (`collab/history.ts`): toont het formulier een stand
   * uit de geschiedenis, dan neemt deze editor die over, ook als hij pas na het kiezen ontstaat (een
   * sectie die tijdens het terugkijken wordt opengeklapt).
   */
  useEffect(() => {
    const doc = fragment.doc
    if (editor === null || doc === null) return
    return registerEditor(doc, editor)
  }, [editor, fragment])

  // De labels van de bronvermeldingen naar de weergave van de knopen (`richtext/reference.ts`).
  useEffect(() => {
    editor?.storage.referentie.labels.set(citationLabels ?? NO_LABELS)
  }, [editor, citationLabels])
  // Het element van de popover, voor `aria-describedby` op de citatie (`CitationPopover`).
  const popoverId = `${useId()}-bronvermelding`
  useEffect(() => {
    if (editor !== null) editor.storage.referentie.popoverId = popoverId
  }, [editor, popoverId])
  // En het id voor een gekopieerde citatie, van dezelfde afnemer als bij invoegen.
  useEffect(() => {
    if (editor !== null && newCitationId !== undefined) editor.storage.referentie.newCitationId = newCitationId
  }, [editor, newCitationId])


  /**
   * Heeft dít veld nu focus? Bepaalt of de balk zijn knoppen toont of inklapt tot een hint
   * (feedback op de eerdere stand: een balk per rich-text-veld, altijd zichtbaar, kost bij
   * meerdere velden op een pagina evenveel ruimte en is dan vooral afleidend). CitatenPOC lost
   * ditzelfde probleem anders op, met één gedeelde werkbalk voor de hele pagina die het laatst
   * gefocuste veld volgt (`EditorToolbar.tsx`); dat vraagt gedeelde state over alle
   * `Richtext`-instanties heen en een nieuwe plek in de paginalayout, een grotere wijziging dan
   * hier gekozen: per veld dezelfde balk laten staan, maar hem laten volgen op de focus van dát
   * veld.
   *
   * `useEditor` rendert dit component zelf al opnieuw bij elke transactie (`@tiptap/react`,
   * intern via `onTransaction`/`onUpdate`), maar niet bij focus of blur zonder wijziging in de
   * tekst — vandaar deze eigen listener.
   */
  /** Hoe vaak er een bronvermelding is ingevoegd, voor de statusregel (`showPlaced`). */
  const [placed, setPlaced] = useState(0)
  const [hasFocus, setHasFocus] = useState(false)
  useEffect(() => {
    if (editor === null) return
    const handleFocus = () => setHasFocus(true)
    const handleBlur = () => setHasFocus(false)
    editor.on('focus', handleFocus)
    editor.on('blur', handleBlur)
    return () => {
      editor.off('focus', handleFocus)
      editor.off('blur', handleBlur)
    }
  }, [editor])

  /**
   * De balk telt ook als "actief" met het formulepaneel open, een opmerkingconcept in de maak
   * of het bronnenpaneel open, ook al ligt de DOM-focus dan niet op dit veld zelf (op het
   * `<math-field>`, op een knop in `Draadpaneel`, of op een veld in `SourcePanel`): anders
   * verdwijnt de balk zodra je in zo'n paneel klikt of typt.
   *
   * Dit is niet alleen cosmetisch. Geen van de eigen knoppen van die panelen ("Plaats de
   * opmerking", "Laat vallen", "Invoegen", "Annuleren") heeft de `preventDefault`-op-mousedown-
   * truc van `Button` hierboven, dus hun mousedown blurt het veld gewoon. Zonder deze aanvulling
   * klapte de balk daardoor middenin die klik in (van een volle, meerdere-rijen-brede
   * knoppenrij naar de smalle hint), en die inklap schuift de pagina omhoog terwijl de
   * muisknop nog ingedrukt is — de knop waarop je net klikte staat dan niet meer onder de muis
   * als de click zelf afgaat, en die mist zijn doel. Gevonden via een test die intermitterend
   * faalde na het toevoegen van de extra werkbalkknoppen (batch 1): meer knoppen op de balk
   * maakt hem hoger, dus de inklapsprong groter, dus de kans dat een klik zijn doel mist ook
   * groter.
   */
  /**
   * Of de focus ergens in dit veld staat: in de tekst, in de werkbalk of in een open menu ervan.
   *
   * Niet alleen de focus van de editor: de werkbalk is een toolbar met één tabstop (`Toolbar.tsx`),
   * en wie met Shift+Tab of de pijltjes van de tekst naar de balk gaat, verlaat de editor. Zou de
   * balk dan inklappen, dan is hij met het toetsenbord onbereikbaar.
   */
  const [within, setWithin] = useState(false)

  /**
   * De zoekbalk van dit veld, of niets (R-166). `signaal` gaat omhoog bij elke Ctrl+F terwijl de balk
   * al open is, zodat het zoekvak de focus terugkrijgt.
   */
  const [find, setFind] = useState<{ signaal: number } | undefined>(undefined)
  const openFind = (): void => setFind((open) => ({ signaal: (open?.signaal ?? 0) + 1 }))

  // Ingeklapt pas na een lopende klik, anders schuift de pagina onder de muis weg (`holdDuringPress.ts`).
  const barActive = useHoldDuringPress(
    within || hasFocus || formula !== undefined || sourcePanel != null || find !== undefined,
  )

  /**
   * Ctrl/Cmd+F met de focus in de tekst, de werkbalk of de zoekbalk van dit veld opent de zoekbalk.
   *
   * Op de omhulling en niet als toets van de editor: een vlak dat alleen te lezen is krijgt geen
   * toetsen van ProseMirror, en juist daar zoekt de toetser. Een ander invoerveld binnen het veld
   * (het antwoord op een opmerking, het formulepaneel) houdt de Ctrl+F van de browser.
   */
  const onFieldKey = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || event.key.toLowerCase() !== 'f') return
    if ((event.target as HTMLElement).closest('.ef-richtext__vlak, .ef-richtext__balk, .ef-zoekbalk') === null) return
    event.preventDefault()
    openFind()
  }

  /** De zoekterm van de vorige keer in dit veld; lokaal, want zoeken is van niemand anders. */
  const [lastQuery, setLastQuery] = useState('')

  const closeFind = (match: { from: number; to: number } | undefined, query: string): void => {
    setFind(undefined)
    setLastQuery(query)
    if (editor === null) return
    if (match !== undefined) editor.commands.setTextSelection(match)
    // `view.focus()` doet niets op een vlak dat niet bewerkbaar is; dan het element zelf.
    if (editor.isEditable) editor.view.focus()
    else editor.view.dom.focus()
  }

  const fieldName = field.label === undefined ? field.id : inLanguage(field.label, language)

  /** De delen van de werkbalk: losse knoppen, scheidingen en drie menu's. */
  /**
   * De knop Opmerking (R-73): voor een schrijver in de werkbalk, voor een meelezer als enige knop in
   * de balk van zijn alleen-lezen vlak. Zonder selectie doet hij niets: een opmerking bij niets is
   * nergens aan te hangen. Uitgeschakeld tonen zou ook kunnen; dat zou de balk laten knipperen bij
   * elke cursorbeweging, en dat is erger.
   */
  const commentPart = (active: NonNullable<typeof editor>): ToolbarPart | undefined => {
    if (editable ? onNewComment === undefined : onCommentViaPod === undefined) return undefined
    return {
      key: 'opmerking',
      label: LABELS.opmerking[language],
      icon: <MessageSquarePlus aria-hidden="true" size={ICON_SIZE} />,
      pressed: active.isActive(COMMENT_MARK),
      onRun: () => {
        // Met de muis blijft de focus in de tekst (`Toolbar.tsx`), dus de klik kan vóór de melding van de
        // laatste selectiestap komen; eerst gelijkzetten (`richtext/domSelection.ts`).
        syncSelectionFromDom(active.view)
        const { from, to } = active.state.selection
        if (from === to) return
        // Alleen het concept: het draadje en het anker komen er pas met het eerste bericht. Een
        // draadje zonder bericht bestaat niet in het model.
        setDraft({
          id: newThreadId?.() ?? `drd-${String(from)}-${String(to)}`,
          citaat: active.state.doc.textBetween(from, to, ' '),
          van: from,
          tot: to,
        })
      },
    }
  }

  const toolbarParts = (active: NonNullable<typeof editor>): readonly ToolbarPart[] => {
    if (!editable) {
      const comment = commentPart(active)
      return comment === undefined ? [] : [comment]
    }
    const chain = () => active.chain().focus()
    const parts: ToolbarPart[] = [
      {
        key: 'tekst',
        label: LABELS.soortTekst[language],
        text: active.isActive('heading')
          ? `${LABELS.kopNiveau[language]} ${String(HEADER_LEVELS.find((level) => active.isActive('heading', { level })) ?? '')}`
          : LABELS.kopTekst[language],
        kind: 'radio',
        items: [
          {
            key: 'alinea',
            label: LABELS.kopTekst[language],
            icon: <Pilcrow aria-hidden="true" size={ICON_SIZE} />,
            pressed: !active.isActive('heading'),
            onRun: () => chain().setParagraph().run(),
          },
          ...HEADER_LEVELS.map((level) => {
            const Icon = HEADER_ICON[level]
            return {
              key: `kop-${String(level)}`,
              label: `${LABELS.kopNiveau[language]} ${String(level)}`,
              icon: <Icon aria-hidden="true" size={ICON_SIZE} />,
              pressed: active.isActive('heading', { level }),
              onRun: () => chain().toggleHeading({ level }).run(),
            }
          }),
        ],
      },
      'scheiding',
      { key: 'vet', label: LABELS.vet[language], icon: <Bold aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('bold'), onRun: () => chain().toggleBold().run() },
      { key: 'cursief', label: LABELS.cursief[language], icon: <Italic aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('italic'), onRun: () => chain().toggleItalic().run() },
      { key: 'onderstrepen', label: LABELS.onderstrepen[language], icon: <UnderlineIcoon aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('underline'), onRun: () => chain().toggleUnderline().run() },
      'scheiding',
      { key: 'lijst', label: LABELS.lijst[language], icon: <List aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('bulletList'), onRun: () => chain().toggleBulletList().run() },
      { key: 'genummerd', label: LABELS.genummerdeLijst[language], icon: <ListOrdered aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('orderedList'), onRun: () => chain().toggleOrderedList().run() },
      'scheiding',
      {
        key: 'bron',
        label: LABELS.bronCiteren[language],
        icon: <BookMarked aria-hidden="true" size={ICON_SIZE} />,
        text: LABELS.bronCiteren[language],
        shortText: LABELS.bron[language],
        onRun: insertCitation,
      },
      {
        key: 'invoegen',
        label: LABELS.invoegen[language],
        text: LABELS.invoegen[language],
        items: [
          { key: 'formule-inline', label: LABELS.formuleInline[language], icon: <Sigma aria-hidden="true" size={ICON_SIZE} />, onRun: () => setFormula({ modus: 'inline', latex: '' }) },
          { key: 'formule-blok', label: LABELS.formuleBlok[language], icon: <SquareFunction aria-hidden="true" size={ICON_SIZE} />, onRun: () => setFormula({ modus: 'blok', latex: '' }) },
          { key: 'figuur', label: LABELS.figuur[language], icon: <ImageIcoon aria-hidden="true" size={ICON_SIZE} />, onRun: insertFigure },
          { key: 'tabel', label: LABELS.tabel[language], icon: <TableIcoon aria-hidden="true" size={ICON_SIZE} />, onRun: () => chain().insertContent(newTable(language, 3, 2)).run() },
          { key: 'lijn', label: LABELS.horizontaleLijn[language], icon: <Minus aria-hidden="true" size={ICON_SIZE} />, onRun: () => chain().setHorizontalRule().run() },
          { key: 'bronvermelding', label: LABELS.referentie[language], icon: <BookMarked aria-hidden="true" size={ICON_SIZE} />, onRun: insertCitation },
        ],
      },
      {
        key: 'meer',
        label: LABELS.meerOpmaak[language],
        text: LABELS.meer[language],
        items: [
          { key: 'doorhalen', label: LABELS.doorhalen[language], icon: <Strikethrough aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('strike'), onRun: () => chain().toggleStrike().run() },
          { key: 'code', label: LABELS.inlineCode[language], icon: <Code aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('code'), onRun: () => chain().toggleCode().run() },
          { key: 'subscript', label: LABELS.subscript[language], icon: <SubscriptIcoon aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('subscript'), onRun: () => chain().toggleSubscript().run() },
          { key: 'superscript', label: LABELS.superscript[language], icon: <SuperscriptIcoon aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('superscript'), onRun: () => chain().toggleSuperscript().run() },
          { key: 'markeren', label: LABELS.markeren[language], icon: <Highlighter aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('highlight'), onRun: () => chain().toggleHighlight().run() },
          { key: 'taaklijst', label: LABELS.taaklijst[language], icon: <ListChecks aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('taskList'), onRun: () => chain().toggleTaskList().run() },
          { key: 'citaatblok', label: LABELS.citaatblok[language], icon: <Quote aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('blockquote'), onRun: () => chain().toggleBlockquote().run() },
          { key: 'codeblok', label: LABELS.codeblok[language], icon: <Code2 aria-hidden="true" size={ICON_SIZE} />, pressed: active.isActive('codeBlock'), onRun: () => chain().toggleCodeBlock().run() },
          { key: 'zoeken', label: `${LABELS.zoekenInVeld[language]} (${findShortcut()})`, icon: <Search aria-hidden="true" size={ICON_SIZE} />, onRun: openFind },
          ...ALIGNMENTS.map((alignment) => {
            const Icon = ALIGNMENT_ICON[alignment]
            return {
              key: `uitlijnen-${alignment}`,
              label: LABELS[`uitlijnen${alignment}`][language],
              icon: <Icon aria-hidden="true" size={ICON_SIZE} />,
              pressed: active.isActive({ textAlign: alignment }),
              onRun: () => chain().setTextAlign(alignment).run(),
            }
          }),
        ],
      },
    ]
    const comment = commentPart(active)
    if (comment !== undefined) parts.push('scheiding', comment)
    return parts
  }

  /** Een bronvermelding invoegen, uit "Bron citeren" en uit het menu Invoegen (R-14, R-50). */
  const insertCitation = (): void => {
    void (async () => {
      const sources = await chooseReference?.()
      if (editor === null) return
      if (sources === undefined || sources.length === 0) {
        editor.commands.focus()
        return
      }
      const citationId = newCitationId?.() ?? `cit-${crypto.randomUUID()}`
      editor.chain().focus().insertContent({ type: 'referentie', attrs: { citatieId: citationId, bronnen: sources } }).run()
      showPlaced(citationId)
    })()
  }

  /**
   * Na de dialoog zien en horen wat er is gebeurd (opdracht van 1 november 2026): de nieuwe
   * bronvermelding licht even op op haar plek in de tekst, en een statusregel zegt het voor wie
   * niet kijkt (R-140). De markering staat op de DOM en niet in het document (`highlight`).
   */
  const showPlaced = (citationId: string): void => {
    if (editor === null) return
    highlight(editor.view.dom.querySelector(`[data-referentie="${CSS.escape(citationId)}"]`))
    setPlaced((count) => count + 1)
  }

  /**
   * Een bronvermelding op de plek van een getypte `@` (zie `handleTextInput`). Staat de `@` er bij
   * het kiezen niet meer (een medeschrijver typte ervoor, of hij is weggehaald), dan komt de
   * bronvermelding waar de cursor staat. Annuleren laat de `@` staan, met de cursor erachter.
   */
  const insertCitationAt = (at: number): void => {
    void (async () => {
      const sources = await chooseReference?.({ zoeken: true })
      if (editor === null || editor.isDestroyed) return
      const doc = editor.state.doc
      const stillThere = at + 1 <= doc.content.size && doc.textBetween(at, at + 1) === '@'
      if (sources === undefined || sources.length === 0) {
        editor.chain().focus(stillThere ? at + 1 : undefined).run()
        return
      }
      const citationId = newCitationId?.() ?? `cit-${crypto.randomUUID()}`
      const node = { type: 'referentie', attrs: { citatieId: citationId, bronnen: sources } }
      if (stillThere) editor.chain().focus().deleteRange({ from: at, to: at + 1 }).insertContentAt(at, node).run()
      else editor.chain().focus().insertContent(node).run()
      showPlaced(citationId)
    })()
  }
  atTrigger.current = editable && chooseReference !== undefined ? insertCitationAt : undefined

  /**
   * De opmerking plaatsen (R-73). Een schrijver zet het draadje in het document en het anker op de
   * tekst waar het concept over ging, niet op de selectie van nu: die kan intussen zijn verschoven.
   * Een meelezer stuurt het bereik naar de pod; het anker komt hier binnen met de update. Daarna licht
   * het anker even op, zodat u ziet waar de opmerking hangt; het gesprek staat in het paneel Opmerkingen.
   */
  const placeComment = (active: NonNullable<typeof editor>, concept: { id: string; citaat: string; van: number; tot: number }, plainText: string): unknown => {
    if (editable && onNewComment !== undefined) {
      onNewComment({ id: concept.id, veldId: field.id, citaat: concept.citaat }, plainText)
      active.chain().setTextSelection({ from: concept.van, to: concept.tot }).zetOpmerking(concept.id).setTextSelection(concept.tot).run()
      setDraft(undefined)
      flashAnchor(active.view.dom, concept.id)
      return undefined
    }
    if (onCommentViaPod === undefined) return undefined
    const binding = (ySyncPluginKey.getState(active.state) as { binding?: { mapping: unknown } } | undefined)?.binding
    if (binding === undefined) return undefined
    const position = (at: number): string =>
      encodeRelativePosition(absolutePositionToRelativePosition(at, fragment, binding.mapping as Parameters<typeof absolutePositionToRelativePosition>[2]))
    return onCommentViaPod({ van: position(concept.van), tot: position(concept.tot) }, plainText).then(({ draad }) => {
      setDraft(undefined)
      flashAnchor(active.view.dom, draad)
    })
  }

  /** Een figuur invoegen (R-14): de afbeelding gaat als verwijzing naar de opslag, niet als bytes. */
  const insertFigure = (): void => {
    void (async () => {
      const chosen = await chooseFigure?.()
      if (editor === null || chosen === undefined) return
      editor.chain().focus().insertContent({ type: 'figuur', attrs: chosen }).run()
    })()
  }

  return (
    <div
      className="ef-richtext"
      data-bewerkbaar={editable ? 'ja' : 'nee'}
      onFocus={() => {
        setWithin(true)
        // Het formulepaneel alvast halen, zodat het er bij de klik op de knop meteen staat
        // (`formulaPanelLoader.ts`). Een fout hier is geen fout: het openen probeert het opnieuw.
        if (editable) preloadFormulaPanel().catch(() => undefined)
      }}
      onBlur={(event) => {
        // Alleen als de focus het veld echt verlaat, en niet bij een stap van de tekst naar de balk.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setWithin(false)
      }}
      onKeyDown={onFieldKey}
    >
      {(editable || selectable) && editor !== null && barActive ? (
        <Toolbar
          label={LABELS.balk[language]}
          controls={link.id}
          parts={toolbarParts(editor)}
          smal={{ houden: NARROW_KEEP, label: LABELS.smalMenu[language], text: '⋯' }}
        />
      ) : null}
      {editable || selectable ? (
        <span className="ef-sr-only" id={toolbarHintId}>
          {editable ? LABELS.balkHint[language] : LABELS.opmerkingHint[language]}
        </span>
      ) : null}

      {editable && editor !== null && within && editor.isActive('table') ? (
        <div className="ef-richtext__tabelbalk" role="toolbar" aria-label={LABELS.tabelbalk[language]}>
          <Button
            active={false}
            disabled={!editor.can().addRowAfter()}
            label={LABELS.rijToevoegen[language]}
            onClick={() => editor.chain().focus().addRowAfter().run()}
          >
            <Plus aria-hidden="true" size={ICON_SIZE} />
            {LABELS.rijKort[language]}
          </Button>
          <Button
            active={false}
            disabled={!editor.can().deleteRow()}
            label={LABELS.rijVerwijderen[language]}
            onClick={() => editor.chain().focus().deleteRow().run()}
          >
            <Minus aria-hidden="true" size={ICON_SIZE} />
            {LABELS.rijKort[language]}
          </Button>
          <Button
            active={false}
            disabled={!editor.can().addColumnAfter()}
            label={LABELS.kolomToevoegen[language]}
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          >
            <Plus aria-hidden="true" size={ICON_SIZE} />
            {LABELS.kolomKort[language]}
          </Button>
          <Button
            active={false}
            disabled={!editor.can().deleteColumn()}
            label={LABELS.kolomVerwijderen[language]}
            onClick={() => editor.chain().focus().deleteColumn().run()}
          >
            <Minus aria-hidden="true" size={ICON_SIZE} />
            {LABELS.kolomKort[language]}
          </Button>
          <Button
            active={false}
            disabled={!editor.can().deleteTable()}
            label={LABELS.tabelVerwijderen[language]}
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            <Trash2 aria-hidden="true" size={ICON_SIZE} />
          </Button>
        </div>
      ) : null}

      {editor !== null && find !== undefined ? (
        <FindBar editor={editor} taal={language} veldNaam={fieldName} signaal={find.signaal} begin={lastQuery} onClose={closeFind} />
      ) : null}

      <EditorContent editor={editor} />
      {/*
        Wie alleen leest heeft geen werkbalk en dus geen menu Meer; met de focus in het veld staat
        hier de ingang naar dezelfde zoekbalk, zodat zoeken ook zonder sneltoets te vinden is.
      */}
      {!editable && editor !== null && within && find === undefined ? (
        <button type="button" className="ef-richtext__zoekknop" onClick={openFind}>
          <Search aria-hidden="true" size={ICON_SIZE} />
          {LABELS.zoekenInTekst[language]}
        </button>
      ) : null}
      {editor === null ? null : (
        <CitationPopover store={editor.storage.referentie.popover} id={popoverId} entries={citationEntries ?? NO_ENTRIES} />
      )}

      <p role="status" className="ef-sr-only">
        {/* Een sleutel per keer, zodat een tweede invoeging opnieuw wordt voorgelezen. */}
        {placed === 0 ? null : <span key={placed}>{LABELS.bronvermeldingGeplaatst[language]}</span>}
      </p>
      <Counter tekst={plainText} limits={limits} taal={language} id={counterId} />
      <RefusedCharactersNotice refused={refused} taal={language} />

      {formula !== undefined && formulaPanelFailed ? (
        // Dezelfde omhulling en knoppen als het paneel zelf, zodat de melding op de plek staat
        // waar het paneel had gestaan.
        <section className="ef-formule-paneel" data-proef="formulepaneel-fout">
          <p role="alert">{LABELS.formulePaneelFout[language]}</p>
          <div className="ef-formule-paneel__acties">
            <button type="button" className="ef-formule-paneel__invoegen" onClick={retryFormulaPanel}>
              {LABELS.opnieuwProberen[language]}
            </button>
            <button type="button" className="ef-formule-paneel__annuleer" onClick={() => setFormula(undefined)}>
              {LABELS.annuleren[language]}
            </button>
          </div>
        </section>
      ) : null}

      {formula === undefined || FormulaPanel === undefined ? null : (
        <FormulaPanel
          // Zonder deze key blijft React dezelfde componentinstantie hergebruiken als je van
          // formule wisselt zonder eerst te sluiten (bijvoorbeeld eerst "Inline formule", dan
          // meteen "Blokformule"): de kop verandert dan wel via de props, maar het mathfield
          // zelf niet, want zijn eigen mount-`useEffect` draait maar één keer. Met deze key
          // per doel (nieuw-inline, nieuw-blok, of de positie van een bestaande knoop) forceert
          // React een echte nieuwe mount bij elke wisseling.
          key={`${formula.modus}-${formula.pos ?? 'nieuw'}`}
          mode={formula.modus}
          latex={formula.latex}
          taal={language}
          onConfirm={(latex) => {
            if (editor === null) return
            const command = editor.chain().focus()
            if (formula.pos === undefined) {
              // Nieuwe formule: invoegen op de huidige selectie.
              if (formula.modus === 'blok') command.insertBlockMath({ latex }).run()
              else command.insertInlineMath({ latex }).run()
            } else {
              // Bestaande knoop bijwerken, op de positie die `handleClickOn` gaf.
              if (formula.modus === 'blok') command.updateBlockMath({ latex, pos: formula.pos }).run()
              else command.updateInlineMath({ latex, pos: formula.pos }).run()
            }
            setFormula(undefined)
          }}
          onCancel={() => setFormula(undefined)}
        />
      )}

      {draft === undefined || editor === null ? null : (
        <CommentDialog
          taal={language}
          citaat={draft.citaat}
          onPlaatsen={(plainText) => placeComment(editor, draft, plainText)}
          onSluit={() => setDraft(undefined)}
          focusTerug={() => {
            // Terug naar de tekst, met de selectie waarover de opmerking ging (WCAG 2.4.3).
            const size = editor.state.doc.content.size
            editor.chain().focus().setTextSelection({ from: Math.min(draft.van, size), to: Math.min(draft.tot, size) }).run()
          }}
        />
      )}

      {sourcePanel}
    </div>
  )
}

const LABELS = {
  balk: { nl: 'Opmaak', en: 'Formatting' },
  zoekenInVeld: { nl: 'Zoeken in dit veld', en: 'Find in this field' },
  zoekenInTekst: { nl: 'Zoeken in tekst', en: 'Find in text' },
  balkHint: {
    nl: 'Met de focus in het veld staat erboven een opmaakwerkbalk; Shift+Tab gaat erheen.',
    en: 'With focus in the field, a formatting toolbar appears above it; Shift+Tab moves to it.',
  },
  opmerking: { nl: 'Opmerking bij de selectie', en: 'Comment on the selection' },
  opmerkingHint: {
    nl: 'U kunt deze tekst lezen, niet wijzigen. Selecteer tekst en kies Opmerking bij de selectie in de balk erboven; Shift+Tab gaat erheen.',
    en: 'You can read this text, not change it. Select text and choose Comment on the selection in the bar above it; Shift+Tab moves to it.',
  },
  soortTekst: { nl: 'Soort tekst', en: 'Text style' },
  bronCiteren: { nl: 'Bron citeren', en: 'Cite a source' },
  bron: { nl: 'Bron', en: 'Cite' },
  smalMenu: { nl: 'Meer opmaak en invoegen', en: 'More formatting and insert' },
  invoegen: { nl: 'Invoegen', en: 'Insert' },
  meer: { nl: 'Meer', en: 'More' },
  meerOpmaak: { nl: 'Meer opmaak', en: 'More formatting' },
  kopTekst: { nl: 'Gewone tekst', en: 'Plain text' },
  kopNiveau: { nl: 'Kop', en: 'Heading' },
  vet: { nl: 'Vet', en: 'Bold' },
  cursief: { nl: 'Cursief', en: 'Italic' },
  onderstrepen: { nl: 'Onderstrepen', en: 'Underline' },
  doorhalen: { nl: 'Doorhalen', en: 'Strikethrough' },
  inlineCode: { nl: 'Code', en: 'Code' },
  lijst: { nl: 'Opsomming', en: 'Bullet list' },
  genummerdeLijst: { nl: 'Genummerde lijst', en: 'Numbered list' },
  citaatblok: { nl: 'Citaatblok', en: 'Blockquote' },
  codeblok: { nl: 'Codeblok', en: 'Code block' },
  horizontaleLijn: { nl: 'Horizontale lijn', en: 'Horizontal rule' },
  taaklijst: { nl: 'Taaklijst', en: 'Task list' },
  subscript: { nl: 'Subscript', en: 'Subscript' },
  superscript: { nl: 'Superscript', en: 'Superscript' },
  markeren: { nl: 'Markeren', en: 'Highlight' },
  uitlijnenleft: { nl: 'Links uitlijnen', en: 'Align left' },
  uitlijnencenter: { nl: 'Centreren', en: 'Align center' },
  uitlijnenright: { nl: 'Rechts uitlijnen', en: 'Align right' },
  uitlijnenjustify: { nl: 'Uitvullen', en: 'Justify' },
  // Beide beginnen met "Formule"/"Formula": de zichtbare knoptekst is voor allebei
  // `KORTE_LABELS.formule` (e2), en die moet in de naam voorkomen (WCAG 2.5.3, dezelfde toets
  // als de test hieronder voor figuur en referentie).
  formuleInline: { nl: 'Formule inline', en: 'Formula inline' },
  formulePaneelFout: {
    nl: 'De formule-editor kon niet worden geladen.',
    en: 'The formula editor could not be loaded.',
  },
  opnieuwProberen: { nl: 'Opnieuw proberen', en: 'Try again' },
  annuleren: { nl: 'Annuleren', en: 'Cancel' },
  formuleBlok: { nl: 'Formule blok', en: 'Formula block' },
  figuur: { nl: 'Figuur invoegen', en: 'Insert figure' },
  referentie: { nl: 'Bronvermelding invoegen', en: 'Insert citation' },
  bronvermeldingGeplaatst: { nl: 'Bronvermelding ingevoegd.', en: 'Citation inserted.' },
  tabel: { nl: 'Tabel invoegen', en: 'Insert table' },
  tabelbalk: { nl: 'Tabelbewerking', en: 'Table editing' },
  rijToevoegen: { nl: 'Rij toevoegen', en: 'Add row' },
  rijVerwijderen: { nl: 'Rij verwijderen', en: 'Delete row' },
  rijKort: { nl: 'rij', en: 'row' },
  kolomToevoegen: { nl: 'Kolom toevoegen', en: 'Add column' },
  kolomVerwijderen: { nl: 'Kolom verwijderen', en: 'Delete column' },
  kolomKort: { nl: 'kolom', en: 'column' },
  tabelVerwijderen: { nl: 'Tabel verwijderen', en: 'Delete table' },
} as const

const COLUMN_HEADER = { nl: 'Kolom', en: 'Column' }

/** De koppenknoppen beperken zich tot 1 t/m 3, naar CitatenPOC's eigen keuze; zie `schema.ts`. */
const HEADER_LEVELS = [1, 2, 3] as const
const HEADER_ICON: Record<(typeof HEADER_LEVELS)[number], typeof Heading1> = {
  1: Heading1,
  2: Heading2,
  3: Heading3,
}

const ALIGNMENTS = ['left', 'center', 'right', 'justify'] as const
const ALIGNMENT_ICON: Record<(typeof ALIGNMENTS)[number], typeof AlignLeft> = {
  left: AlignLeft,
  center: AlignCenter,
  right: AlignRight,
  justify: AlignJustify,
}

/** De sneltoets voor zoeken zoals de invuller hem op zijn toetsenbord ziet. */
function findShortcut(): string {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘F' : 'Ctrl+F'
}

/** Iconen dragen zelf geen naam (`aria-hidden`): de knop krijgt zijn naam via `label`. */
const ICON_SIZE = 16


/**
 * Een tabel met ingevulde kopcellen, niet leeg.
 *
 * `insertTable` uit `@tiptap/extension-table` levert lege koppen, en een lege `th` is een
 * axe-overtreding (`empty-table-header`): een tabelkop zonder tekst is voor een schermlezer
 * een kop zonder naam. Dus zelf opbouwen met `insertContent`, met een startlabel per kolom dat
 * de invuller meteen kan overschrijven.
 */
function newTable(language: Language, columns: number, bodyRows: number): Record<string, unknown> {
  const headerRow = {
    type: 'tableRow',
    content: Array.from({ length: columns }, (_, index) => ({
      type: 'tableHeader',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: `${COLUMN_HEADER[language]} ${index + 1}` }] }],
    })),
  }
  const body = Array.from({ length: bodyRows }, () => ({
    type: 'tableRow',
    content: Array.from({ length: columns }, () => ({ type: 'tableCell', content: [{ type: 'paragraph' }] })),
  }))
  return { type: 'table', content: [headerRow, ...body] }
}

function Button({
  active,
  label,
  onClick,
  children,
  labeled = false,
  disabled = false,
}: {
  active: boolean
  label: string
  onClick: () => void
  children: ReactNode
  /** Toont zichtbare tekst naast het icoon i.p.v. alleen het icoon (e2, R-157): Formule/Figuur/Bronvermelding. */
  labeled?: boolean
  /**
   * Voor de tabel-subbalk: `editor.can()` kan fout-positief zijn op bijvoorbeeld een tabel
   * met één rij, maar het onderliggende commando doet dan veilig niets (naar CitatenPOC's
   * eigen redenering voor dezelfde knoppen).
   */
  disabled?: boolean
}): ReactNode {
  return (
    <button
      type="button"
      className={labeled ? 'ef-richtext__knop ef-richtext__knop--gelabeld' : 'ef-richtext__knop'}
      aria-pressed={active}
      aria-label={label}
      title={label}
      disabled={disabled}
      // Zonder dit blurt het veld op de mousedown, vóór de click: de balk (die op focus
      // inklapt, zie `hasFocus` in Richtext.tsx) verdwijnt dan al terwijl je nog aan het
      // klikken bent, en de knop kan van onder de muis wegvallen vóór zijn click-handler
      // draait. Naar CitatenPOC's eigen `ToolButton` (`EditorToolbar.tsx`), met dezelfde reden.
      onMouseDown={(event: ReactMouseEvent<HTMLButtonElement>) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

/** Eén lege verzameling, zodat een veld zonder editor geen nieuwe per hertekening maakt. */
/**
 * Het anker van een opmerking even laten oplichten, als het er is of binnen een paar seconden komt
 * (bij een meelezer met de update van de pod). Zonder beweging bij `prefers-reduced-motion`; de
 * opmaak staat in `schil.css` (`[data-gemarkeerd]`).
 */
function flashAnchor(root: HTMLElement, threadId: string): void {
  const selector = `[data-draad-id="${CSS.escape(threadId)}"]`
  const started = Date.now()
  const attempt = (): void => {
    const element = root.querySelector<HTMLElement>(selector)
    if (element === null) {
      if (Date.now() - started < 5000) window.setTimeout(attempt, 100)
      return
    }
    const soft = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    element.scrollIntoView({ behavior: soft ? 'smooth' : 'auto', block: 'nearest' })
    for (const part of root.querySelectorAll<HTMLElement>(selector)) part.setAttribute('data-gemarkeerd', 'ja')
    window.setTimeout(() => {
      for (const part of root.querySelectorAll<HTMLElement>(selector)) part.removeAttribute('data-gemarkeerd')
    }, 2000)
  }
  attempt()
}
/** Geen labels: elke bronvermelding toont dan de plaatshouder. Eén referentie, zodat er niets opnieuw tekent. */
const NO_LABELS: ReadonlyMap<string, string> = new Map()
/** Wat op een smal veld in de rij van de werkbalk blijft (ontwerp "Telefoon": B, I, lijst, Bron, en Opmerking sinds besluit 12.41, ⋯). */
const NARROW_KEEP: readonly string[] = ['vet', 'cursief', 'lijst', 'bron', 'opmerking']

const NO_ENTRIES: ReadonlyMap<string, readonly string[]> = new Map()
